/**
 * RatingTimeline Komponente
 * Visualisiert den CherryScore (Gesamtbewertung) als horizontalen Zeitstrahl
 * Verwendet 0-5 Sterne System (höher = besser)
 *
 * Optional können zusätzlich die drei Level-Linien (Agentur, Team, Chemie)
 * sowie einzelne Kriterien-Linien eingeblendet werden.
 */

import { useState, useRef, memo, useMemo, useCallback, useId } from 'react';
import { calculateCherryScore, formatDate, formatDateTime, getScoreLevel, getScoreColorClass } from '@/utils';
import { calculateAgencyLevelScore, calculateTeamLevelScore, calculateChemistryLevelScore } from '@/utils/stars';
import { AGENCY_LEVEL_CONFIG, TEAM_LEVEL_CONFIG, CHEMISTRY_LEVEL_CONFIG, EVALUATION_LEVELS } from '@/config';
import { UserAvatar } from './LazyImage';
import { Modal } from './Modal';
import type { Rating, EvaluationLevel } from '@/types';

// Star scale constants
const MIN_STARS = 0;
const MAX_STARS = 5;
const STAR_RANGE = MAX_STARS - MIN_STARS;

// Overlay levels (excluding longlist — timeline handles the 3 standard levels)
type OverlayLevel = 'agency' | 'team' | 'chemistry';

interface LevelStyle {
  id: OverlayLevel;
  label: string;
  icon: string;
  color: string;
  lightColor: string;
  bgClass: string;
  borderClass: string;
  textClass: string;
}

const LEVEL_STYLES: Record<OverlayLevel, LevelStyle> = {
  agency: {
    id: 'agency',
    label: AGENCY_LEVEL_CONFIG.shortLabel,
    icon: AGENCY_LEVEL_CONFIG.icon,
    color: 'rgb(59, 130, 246)', // blue-500
    lightColor: 'rgba(147, 197, 253, 0.9)', // blue-300
    bgClass: 'bg-blue-50',
    borderClass: 'border-blue-400',
    textClass: 'text-blue-700',
  },
  team: {
    id: 'team',
    label: TEAM_LEVEL_CONFIG.shortLabel,
    icon: TEAM_LEVEL_CONFIG.icon,
    color: 'rgb(168, 85, 247)', // purple-500
    lightColor: 'rgba(216, 180, 254, 0.9)', // purple-300
    bgClass: 'bg-purple-50',
    borderClass: 'border-purple-400',
    textClass: 'text-purple-700',
  },
  chemistry: {
    id: 'chemistry',
    label: CHEMISTRY_LEVEL_CONFIG.shortLabel,
    icon: CHEMISTRY_LEVEL_CONFIG.icon,
    color: 'rgb(20, 184, 166)', // teal-500
    lightColor: 'rgba(94, 234, 212, 0.9)', // teal-300
    bgClass: 'bg-teal-50',
    borderClass: 'border-teal-400',
    textClass: 'text-teal-700',
  },
};

const OVERLAY_LEVELS: OverlayLevel[] = ['agency', 'team', 'chemistry'];

// Map each overlay level to its main criteria from config
const LEVEL_CRITERIA: Record<OverlayLevel, ReadonlyArray<{ id: string; label: string; icon: string }>> = {
  agency: AGENCY_LEVEL_CONFIG.mainCriteria.map((c) => ({ id: c.id, label: c.label, icon: c.icon })),
  team: TEAM_LEVEL_CONFIG.mainCriteria.map((c) => ({ id: c.id, label: c.label, icon: c.icon })),
  chemistry: CHEMISTRY_LEVEL_CONFIG.mainCriteria.map((c) => ({ id: c.id, label: c.label, icon: c.icon })),
};

interface RatingTimelineProps {
  ratings: Rating[];
  /** Callback when a rating data point is clicked - receives the index in the original (descending) array */
  onRatingSelect?: (index: number) => void;
  /** Index of the currently selected rating in the original (descending) array */
  selectedIndex?: number;
}

// Get level score helper (used in detail modal)
const getLevelScore = (scores: Rating['scores'], level: EvaluationLevel): number | null => {
  switch (level) {
    case 'agency':
      return calculateAgencyLevelScore(scores.agency);
    case 'team':
      return calculateTeamLevelScore(scores.team);
    case 'chemistry':
      return calculateChemistryLevelScore(scores.chemistry);
    default:
      return null;
  }
};

// Raw score extractors per level (null if not rated, 0 = "Nicht bewertbar" counts as missing)
const getOverlayLevelScore = (scores: Rating['scores'], level: OverlayLevel): number | null => {
  switch (level) {
    case 'agency':
      return calculateAgencyLevelScore(scores.agency);
    case 'team':
      return calculateTeamLevelScore(scores.team);
    case 'chemistry':
      return calculateChemistryLevelScore(scores.chemistry);
  }
};

// Get a single criterion score. Returns null for missing / 0 ("Nicht bewertbar").
const getCriterionScore = (scores: Rating['scores'], level: OverlayLevel, criterionId: string): number | null => {
  const mainMap =
    level === 'agency' ? scores.agency.main :
    level === 'team' ? scores.team.main :
    scores.chemistry.main;
  const value = (mainMap as Record<string, number | undefined>)[criterionId];
  if (value === undefined || value === null || value === 0) return null;
  return value;
};

interface SeriesPoint {
  x: number;
  y: number;
  value: number;
}

/**
 * Build an SVG path from points, starting a new sub-path (M) at the beginning
 * and whenever a gap (null) is encountered in the input stream.
 *
 * Expects an array where positions are aligned to the x-axis; use null for gaps.
 */
function buildPath(points: ReadonlyArray<SeriesPoint | null>): string {
  const segments: string[] = [];
  let inSegment = false;
  for (const p of points) {
    if (p === null) {
      inSegment = false;
      continue;
    }
    if (!inSegment) {
      segments.push(`M ${p.x} ${p.y}`);
      inSegment = true;
    } else {
      segments.push(`L ${p.x} ${p.y}`);
    }
  }
  return segments.join(' ');
}

export const RatingTimeline = memo(function RatingTimeline({ ratings, onRatingSelect, selectedIndex: _selectedIndex }: RatingTimelineProps) {
  void _selectedIndex;
  const [hoveredRating, setHoveredRating] = useState<Rating | null>(null);
  const [hoveredPosition, setHoveredPosition] = useState<{ x: number; y: number } | null>(null);
  const [selectedRating, setSelectedRating] = useState<Rating | null>(null);
  const [showDetails, setShowDetails] = useState(false);
  const [expandedModalLevels, setExpandedModalLevels] = useState<Set<EvaluationLevel>>(new Set());

  const toggleModalLevel = useCallback((level: EvaluationLevel) => {
    setExpandedModalLevels((prev) => {
      const next = new Set(prev);
      if (next.has(level)) next.delete(level);
      else next.add(level);
      return next;
    });
  }, []);
  const containerRef = useRef<HTMLDivElement>(null);

  // Overlay state: which level lines are visible
  const [enabledLevels, setEnabledLevels] = useState<Set<OverlayLevel>>(new Set());
  // Which criterion lines are visible, keyed by "${level}:${criterionId}" so
  // criteria from different levels cannot collide (e.g. chemistry-the-level vs
  // chemistry-the-criterion, or if future labels happen to match).
  const [enabledCriteria, setEnabledCriteria] = useState<Set<string>>(new Set());
  // Which level disclosure sections are expanded (showing criterion chips)
  const [expandedLevels, setExpandedLevels] = useState<Set<OverlayLevel>>(new Set());


  // NOTE: Close-on-click-outside is handled by the shared Modal component
  // (backdrop click + ESC). No extra listener required here.

  // Memoize sorted ratings
  const sortedRatings = useMemo(() => {
    if (ratings.length === 0) return [];
    return [...ratings].reverse();
  }, [ratings]);

  // Memoized hover handlers
  const handleMouseEnter = useCallback((rating: Rating, rect: DOMRect) => {
    setHoveredRating(rating);
    setHoveredPosition({ x: rect.left + rect.width / 2, y: rect.top });
  }, []);

  const handleMouseLeave = useCallback(() => {
    setHoveredRating(null);
    setHoveredPosition(null);
  }, []);

  const handleRatingClick = useCallback((rating: Rating, sortedIndex: number) => {
    setSelectedRating((prev) => prev?.id === rating.id ? null : rating);
    // Call external handler with original array index (sortedRatings is reversed)
    if (onRatingSelect) {
      const originalIndex = sortedRatings.length - 1 - sortedIndex;
      onRatingSelect(originalIndex);
    }
  }, [onRatingSelect, sortedRatings.length]);

  const handleCloseModal = useCallback(() => {
    setSelectedRating(null);
    setShowDetails(false);
  }, []);

  const toggleDetails = useCallback(() => {
    setShowDetails((prev) => !prev);
  }, []);

  // Toolbar: Panel open/close + overlay toggles
  const [isPanelOpen, setIsPanelOpen] = useState(false);

  // Unique gradient id so multiple RatingTimeline instances on the same page
  // don't collide (SVG ids are document-global).
  const reactId = useId();
  const gradientId = `areaGradient-${reactId.replace(/:/g, '')}`;

  const toggleLevel = useCallback((level: OverlayLevel) => {
    setEnabledLevels((prev) => {
      const next = new Set(prev);
      const wasOn = next.has(level);
      if (wasOn) {
        // Turning a level OFF also clears its criterion lines and disclosure,
        // so the criterion chips disappear instead of lingering.
        next.delete(level);
        setExpandedLevels((prevExp) => {
          const nextExp = new Set(prevExp);
          nextExp.delete(level);
          return nextExp;
        });
        setEnabledCriteria((prevCrit) => {
          const nextCrit = new Set(prevCrit);
          for (const key of nextCrit) {
            if (key.startsWith(`${level}:`)) nextCrit.delete(key);
          }
          return nextCrit;
        });
      } else {
        next.add(level);
      }
      return next;
    });
  }, []);

  const toggleLevelExpanded = useCallback((level: OverlayLevel) => {
    setExpandedLevels((prev) => {
      const next = new Set(prev);
      if (next.has(level)) next.delete(level);
      else next.add(level);
      return next;
    });
  }, []);

  const toggleCriterion = useCallback((level: OverlayLevel, criterionId: string) => {
    const key = `${level}:${criterionId}`;
    setEnabledCriteria((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  }, []);

  const resetAllOverlays = useCallback(() => {
    setEnabledLevels(new Set());
    setExpandedLevels(new Set());
    setEnabledCriteria(new Set());
  }, []);

  // Chart dimensions (must be declared before hooks that use them)
  const graphHeight = 120;
  const padding = 20;

  // Y-Position berechnen für Sterne (0-5) - höhere Werte = besser = oben
  const getY = useCallback((score: number): number => {
    const normalized = (MAX_STARS - score) / STAR_RANGE;
    return padding + normalized * (graphHeight - 2 * padding);
  }, []);

  // Y-Position als Prozent für HTML-Overlay
  const getYPercent = useCallback((score: number): number => {
    const normalized = (MAX_STARS - score) / STAR_RANGE;
    return (padding + normalized * (graphHeight - 2 * padding)) / graphHeight * 100;
  }, []);

  // X-Positionen gleichmäßig verteilen
  const getX = useCallback((index: number): number => {
    if (sortedRatings.length === 1) return 50;
    return (index / (sortedRatings.length - 1)) * 100;
  }, [sortedRatings.length]);

  /**
   * Compute points for a series. Returns array aligned with sortedRatings
   * where each entry is either a SeriesPoint or null (gap for missing value).
   */
  const computeSeries = useCallback(
    (extractor: (r: Rating) => number | null): Array<SeriesPoint | null> => {
      return sortedRatings.map((rating, index) => {
        const value = extractor(rating);
        if (value === null || Number.isNaN(value)) return null;
        return { x: getX(index), y: getY(value), value };
      });
    },
    [sortedRatings, getX, getY],
  );

  // CherryScore main line (always visible)
  const cherrySeries = useMemo(
    () => computeSeries((r) => calculateCherryScore(r.scores)),
    [computeSeries],
  );

  if (ratings.length === 0) return null;

  // CherryScore SVG paths (main line + area fill) — use plain L path (no gaps expected since CherryScore is always calculable if ANY criterion is rated; but defend with buildPath).
  const cherryLinePath = buildPath(cherrySeries);
  // For area fill, only works if we have a contiguous series. Build from first to last non-null.
  const firstCherry = cherrySeries.find((p): p is SeriesPoint => p !== null);
  const lastCherry = [...cherrySeries].reverse().find((p): p is SeriesPoint => p !== null);
  const cherryAreaPath = (firstCherry && lastCherry)
    ? `${cherryLinePath} L ${lastCherry.x} ${graphHeight - padding} L ${firstCherry.x} ${graphHeight - padding} Z`
    : '';

  // Legend entries (CherryScore + active levels + active criteria)
  interface LegendEntry {
    key: string;
    label: string;
    color: string;
    dashed?: boolean;
  }
  const legend: LegendEntry[] = [
    { key: 'cherry', label: 'CherryScore', color: 'rgb(190, 24, 93)' },
  ];
  for (const level of OVERLAY_LEVELS) {
    if (!enabledLevels.has(level)) continue;
    legend.push({ key: level, label: LEVEL_STYLES[level].label, color: LEVEL_STYLES[level].color });
  }
  for (const level of OVERLAY_LEVELS) {
    LEVEL_CRITERIA[level].forEach((crit) => {
      const key = `${level}:${crit.id}`;
      if (!enabledCriteria.has(key)) return;
      legend.push({
        key,
        label: `${LEVEL_STYLES[level].label} · ${crit.label}`,
        color: LEVEL_STYLES[level].color,
        dashed: true,
      });
    });
  }

  const activeOverlayCount = enabledLevels.size + enabledCriteria.size;

  return (
    <div className="space-y-4" ref={containerRef}>
      {/* Overlay Controls — single trigger opens a configuration panel */}
      <div className="space-y-2">
        <div className="flex flex-wrap items-center gap-2">
          <button
            type="button"
            onClick={() => setIsPanelOpen((prev) => !prev)}
            aria-expanded={isPanelOpen}
            className={`inline-flex items-center gap-2 px-3 py-1.5 text-xs font-medium rounded-lg border transition-colors ${
              activeOverlayCount > 0 || isPanelOpen
                ? 'bg-cherry-50 border-cherry-300 text-cherry-700'
                : 'bg-white border-charcoal-200 text-charcoal-700 hover:bg-charcoal-50'
            }`}
          >
            <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 6h18M7 12h10M10 18h4" />
            </svg>
            <span>Zusätzliche Linien</span>
            {activeOverlayCount > 0 && (
              <span className="inline-flex items-center justify-center min-w-5 h-5 px-1.5 rounded-full bg-cherry-600 text-white text-xs font-bold">
                {activeOverlayCount}
              </span>
            )}
            <svg
              className={`w-3 h-3 transition-transform ${isPanelOpen ? 'rotate-180' : ''}`}
              fill="none" stroke="currentColor" viewBox="0 0 24 24"
            >
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M19 9l-7 7-7-7" />
            </svg>
          </button>

          {activeOverlayCount > 0 && (
            <button
              type="button"
              onClick={resetAllOverlays}
              className="text-xs text-charcoal-500 hover:text-cherry-600 underline underline-offset-2"
            >
              Zurücksetzen
            </button>
          )}

          {/* Compact inline legend when panel is closed and overlays active */}
          {!isPanelOpen && legend.length > 1 && (
            <div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-xs text-charcoal-600">
              {legend.map((entry) => (
                <div key={entry.key} className="inline-flex items-center gap-1.5">
                  <span
                    className="inline-block shrink-0"
                    style={{
                      width: 16,
                      height: 0,
                      borderTop: `${entry.dashed ? '2px dashed' : '2px solid'} ${entry.color}`,
                    }}
                    aria-hidden="true"
                  />
                  <span>{entry.label}</span>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Configuration panel — one section per level, criteria-chips inside each */}
        {isPanelOpen && (
          <div className="p-3 rounded-xl bg-charcoal-50/60 border border-charcoal-200 space-y-2">
            {OVERLAY_LEVELS.map((level) => {
              const style = LEVEL_STYLES[level];
              const isOn = enabledLevels.has(level);
              const isExpanded = expandedLevels.has(level);
              const levelCriteriaKeys = LEVEL_CRITERIA[level].map((c) => `${level}:${c.id}`);
              const activeCriteriaForLevel = levelCriteriaKeys.filter((k) => enabledCriteria.has(k)).length;
              return (
                <div
                  key={level}
                  className={`rounded-lg border transition-colors ${
                    isOn ? `${style.bgClass} ${style.borderClass}` : 'bg-white border-charcoal-200'
                  }`}
                >
                  <div className="flex items-center gap-2 p-2">
                    <button
                      type="button"
                      role="checkbox"
                      aria-checked={isOn}
                      onClick={() => toggleLevel(level)}
                      className="inline-flex items-center gap-2 flex-1 min-w-0"
                    >
                      <span
                        className={`w-4 h-4 rounded border-2 inline-flex items-center justify-center shrink-0 ${
                          isOn ? 'border-transparent' : 'border-charcoal-300 bg-white'
                        }`}
                        style={isOn ? { backgroundColor: style.color } : undefined}
                      >
                        {isOn && (
                          <svg className="w-3 h-3 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" />
                          </svg>
                        )}
                      </span>
                      <span className={`text-sm font-medium ${isOn ? style.textClass : 'text-charcoal-700'}`}>
                        {style.icon} {style.label}
                      </span>
                      {activeCriteriaForLevel > 0 && (
                        <span className={`text-xs ${style.textClass}`}>
                          (+{activeCriteriaForLevel} Kriterien)
                        </span>
                      )}
                    </button>
                    <button
                      type="button"
                      aria-label={`${style.label} Kriterien ${isExpanded ? 'ausblenden' : 'einblenden'}`}
                      aria-expanded={isExpanded}
                      aria-controls={`timeline-panel-criteria-${level}`}
                      onClick={() => toggleLevelExpanded(level)}
                      className="p-1 rounded hover:bg-charcoal-100 text-charcoal-500 shrink-0"
                    >
                      <svg
                        className={`w-4 h-4 transition-transform ${isExpanded ? 'rotate-180' : ''}`}
                        fill="none" stroke="currentColor" viewBox="0 0 24 24"
                      >
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                      </svg>
                    </button>
                  </div>
                  {isExpanded && (
                    <div id={`timeline-panel-criteria-${level}`} className="px-2 pb-2 pt-1 flex flex-wrap gap-1.5">
                      {LEVEL_CRITERIA[level].map((crit) => {
                        const chipKey = `${level}:${crit.id}`;
                        const chipOn = enabledCriteria.has(chipKey);
                        return (
                          <button
                            key={chipKey}
                            type="button"
                            role="checkbox"
                            aria-checked={chipOn}
                            onClick={() => toggleCriterion(level, crit.id)}
                            className={`inline-flex items-center gap-1 text-xs px-2 py-1 rounded-md border transition-colors ${
                              chipOn
                                ? 'border-transparent text-white'
                                : 'bg-white border-charcoal-200 text-charcoal-600 hover:bg-charcoal-50'
                            }`}
                            style={chipOn ? { backgroundColor: style.color } : undefined}
                          >
                            <span aria-hidden="true">{crit.icon}</span>
                            <span>{crit.label}</span>
                          </button>
                        );
                      })}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}

        {/* Full legend inside panel */}
        {isPanelOpen && legend.length > 1 && (
          <div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-xs text-charcoal-600 px-1">
            <span className="font-medium text-charcoal-500">Legende:</span>
            {legend.map((entry) => (
              <div key={entry.key} className="inline-flex items-center gap-1.5">
                <span
                  className="inline-block shrink-0"
                  style={{
                    width: 16,
                    height: 0,
                    borderTop: `${entry.dashed ? '2px dashed' : '2px solid'} ${entry.color}`,
                  }}
                  aria-hidden="true"
                />
                <span>{entry.label}</span>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* SVG Timeline Graph */}
      <div className="relative h-32">
        <svg
          viewBox={`0 0 100 ${graphHeight}`}
          preserveAspectRatio="none"
          className="w-full h-full"
        >
          <defs>
            <linearGradient id={gradientId} x1="0" y1="0" x2="0" y2="1">
              <stop offset="0%" stopColor="rgb(190, 24, 93)" stopOpacity="0.3" />
              <stop offset="100%" stopColor="rgb(190, 24, 93)" stopOpacity="0.05" />
            </linearGradient>
          </defs>

          {/* Horizontale Hilfslinien */}
          <line
            x1="0"
            y1={padding}
            x2="100"
            y2={padding}
            stroke="#e5e7eb"
            strokeWidth="0.5"
            vectorEffect="non-scaling-stroke"
          />
          <line
            x1="0"
            y1={graphHeight / 2}
            x2="100"
            y2={graphHeight / 2}
            stroke="#e5e7eb"
            strokeWidth="0.5"
            vectorEffect="non-scaling-stroke"
          />
          <line
            x1="0"
            y1={graphHeight - padding}
            x2="100"
            y2={graphHeight - padding}
            stroke="#e5e7eb"
            strokeWidth="0.5"
            vectorEffect="non-scaling-stroke"
          />

          {/* Bereich unter der CherryScore-Linie */}
          {cherryAreaPath && <path d={cherryAreaPath} fill={`url(#${gradientId})`} />}

          {/* Hauptlinie (CherryScore) */}
          <path
            d={cherryLinePath}
            fill="none"
            stroke="rgb(190, 24, 93)"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
            vectorEffect="non-scaling-stroke"
          />
        </svg>

        {/* Datenpunkte als HTML-Elemente (bleiben rund) */}
        {sortedRatings.map((rating, index) => {
          const x = getX(index);
          const score = calculateCherryScore(rating.scores);
          const y = getYPercent(score);
          return (
            <button
              key={rating.id}
              type="button"
              aria-label={`Bewertung vom ${formatDate(rating.createdAt)}, Score: ${score.toFixed(1)}`}
              className={`group absolute min-w-11 min-h-11 flex items-center justify-center transform -translate-x-1/2 -translate-y-1/2 cursor-pointer transition-all duration-150 hover:z-20 focus:z-20 focus:outline-none ${
                selectedRating?.id === rating.id ? 'z-20' : ''
              }`}
              style={{
                left: `${x}%`,
                top: `${y}%`,
              }}
              onMouseEnter={(e) => handleMouseEnter(rating, e.currentTarget.getBoundingClientRect())}
              onMouseLeave={handleMouseLeave}
              onFocus={(e) => handleMouseEnter(rating, e.currentTarget.getBoundingClientRect())}
              onBlur={handleMouseLeave}
              onClick={() => handleRatingClick(rating, index)}
            >
              <span className={`w-3 h-3 rounded-full bg-white border-2 shadow-sm transition-transform ${
                selectedRating?.id === rating.id ? 'border-cherry-800 scale-150' : 'border-cherry-600 hover:scale-150 group-focus:scale-150 group-focus:ring-2 group-focus:ring-cherry-600 group-focus:ring-offset-2'
              }`} />
            </button>
          );
        })}

        {/* Hover Tooltip */}
        {hoveredRating && hoveredPosition && !selectedRating && (
          <div
            className="fixed z-50 px-3 py-2 bg-charcoal-800 text-white text-xs rounded-lg shadow-lg pointer-events-none"
            style={{
              left: Math.max(80, Math.min(hoveredPosition.x, window.innerWidth - 80)),
              top: hoveredPosition.y > 80 ? hoveredPosition.y - 8 : hoveredPosition.y + 48,
              transform: `translateX(-50%) ${hoveredPosition.y > 80 ? 'translateY(-100%)' : ''}`,
            }}
          >
            <div className="font-medium">
              {hoveredRating.ratedBy?.displayName || 'Unbekannt'}
            </div>
            <div className="text-charcoal-100">
              {formatDate(hoveredRating.createdAt)}
            </div>
            <div className="text-cherry-200 font-bold">
              Score: {calculateCherryScore(hoveredRating.scores).toFixed(1)} ★
            </div>
            <div className={`absolute left-1/2 transform -translate-x-1/2 ${hoveredPosition.y > 80 ? 'bottom-0 translate-y-full' : 'top-0 -translate-y-full rotate-180'}`}>
              <div className="w-0 h-0 border-l-4 border-r-4 border-t-4 border-l-transparent border-r-transparent border-t-charcoal-800" />
            </div>
          </div>
        )}

        {/* Click Detail Overlay - Shared Modal (focus trap, ESC, role=dialog) */}
        {selectedRating && (() => {
          const modalCherryScore = calculateCherryScore(selectedRating.scores);
          const modalScoreLabel =
            getScoreLevel(modalCherryScore) === 'good' ? 'sehr gut' :
            getScoreLevel(modalCherryScore) === 'medium' ? 'mittel' : 'schwach';

          return (
            <Modal
              isOpen={!!selectedRating}
              onClose={handleCloseModal}
              title={selectedRating.ratedBy?.displayName || 'Unbekannt'}
              subtitle={formatDateTime(selectedRating.createdAt)}
              icon={
                selectedRating.ratedBy ? (
                  <UserAvatar
                    avatar={selectedRating.ratedBy.avatar}
                    displayName={selectedRating.ratedBy.displayName}
                    size="md"
                  />
                ) : (
                  <div className="w-10 h-10 rounded-full bg-charcoal-200 flex items-center justify-center text-charcoal-500">?</div>
                )
              }
              size="lg"
              flexBody
              maxHeight="80vh"
            >
              <div className="p-5 space-y-4">
                {/* Prominent CherryScore */}
                <div className="flex items-center justify-center gap-3 p-4 bg-cherry-50 rounded-xl">
                  <div className="text-center">
                    <p className={`text-4xl font-bold ${getScoreColorClass(modalCherryScore)}`}>
                      {modalCherryScore.toFixed(1)}
                      <span className="text-amber-500 ml-1">★</span>
                      <span className="sr-only"> ({modalScoreLabel})</span>
                    </p>
                    <p className="text-xs text-cherry-700 font-medium mt-1">CherryScore</p>
                  </div>
                </div>

                {/* Expandable Level Rows (Drilldown) */}
                <div className="space-y-2">
                  {EVALUATION_LEVELS.map((level) => {
                    const score = getLevelScore(selectedRating.scores, level.id);
                    const levelLabel = score !== null
                      ? (getScoreLevel(score) === 'good' ? 'sehr gut'
                         : getScoreLevel(score) === 'medium' ? 'mittel' : 'schwach')
                      : null;
                    const isExpanded = expandedModalLevels.has(level.id);
                    const levelScoresMap: Record<string, number | undefined> =
                      (selectedRating.scores[level.id])?.main ?? {};
                    const panelId = `rating-modal-level-${selectedRating.id}-${level.id}`;
                    return (
                      <div key={level.id} className="bg-charcoal-50 rounded-xl overflow-hidden">
                        <button
                          type="button"
                          onClick={() => toggleModalLevel(level.id)}
                          aria-expanded={isExpanded}
                          aria-controls={panelId}
                          className="w-full flex items-center justify-between p-3 hover:bg-charcoal-100 transition-colors text-left focus:outline-none focus:ring-2 focus:ring-cherry-600 focus:ring-offset-2 rounded-xl"
                        >
                          <div className="flex items-center gap-2">
                            <svg className={`w-4 h-4 text-charcoal-400 transition-transform ${isExpanded ? 'rotate-90' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                            </svg>
                            <span className="text-lg">{level.icon}</span>
                            <span className="text-sm font-medium text-charcoal-800">{level.shortLabel}</span>
                          </div>
                          <span className={`text-base font-bold ${score !== null ? getScoreColorClass(score) : 'text-charcoal-400'}`}>
                            {score !== null ? `${score.toFixed(1)} ★` : '—'}
                            {levelLabel && <span className="sr-only"> ({levelLabel})</span>}
                          </span>
                        </button>
                        {isExpanded && (
                          <div id={panelId} className="px-3 pb-3 pt-1 space-y-1.5 border-t border-charcoal-200/70">
                            {level.mainCriteria.map((criterion) => {
                              const critScore = levelScoresMap[criterion.id];
                              const hasScore = typeof critScore === 'number' && critScore > 0;
                              return (
                                <div key={criterion.id} className="flex items-center justify-between pl-6 pr-2 py-1.5">
                                  <div className="flex items-center gap-2 min-w-0">
                                    <span className="text-sm shrink-0">{criterion.icon}</span>
                                    <span className="text-xs text-charcoal-700 truncate">{criterion.label}</span>
                                  </div>
                                  <span className={`text-xs font-semibold shrink-0 ${hasScore ? getScoreColorClass(critScore) : 'text-charcoal-400'}`}>
                                    {hasScore ? `${critScore} ★` : '—'}
                                  </span>
                                </div>
                              );
                            })}
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>

                {/* Details Section */}
                <button
                  type="button"
                  onClick={toggleDetails}
                  aria-expanded={showDetails}
                  aria-controls="rating-timeline-modal-details"
                  className="w-full flex items-center justify-between text-sm text-cherry-600 hover:text-cherry-700 font-medium py-3 border-t border-charcoal-100 focus:outline-none focus:ring-2 focus:ring-cherry-600 focus:ring-offset-2 rounded"
                >
                  <span>{showDetails ? 'Kommentare ausblenden' : 'Kommentare anzeigen'}</span>
                  <svg className={`w-5 h-5 transition-transform ${showDetails ? 'rotate-180' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                  </svg>
                </button>

                {/* Detailed Notes */}
                {showDetails && (
                  <div id="rating-timeline-modal-details" className="space-y-3 pt-3">
                    {/* Overall Notes */}
                    {selectedRating.overallNotes && (
                      <div className="p-4 bg-cherry-50 rounded-xl">
                        <p className="text-sm font-medium text-cherry-700 mb-2">Gesamtkommentar</p>
                        <p className="text-sm text-charcoal-700">
                          "{selectedRating.overallNotes}"
                        </p>
                      </div>
                    )}

                    {/* Level-specific notes */}
                    {selectedRating.notes && EVALUATION_LEVELS.map((level) => {
                      const levelNotes = selectedRating.notes?.[level.id];
                      if (!levelNotes) return null;

                      const hasNotes = typeof levelNotes === 'object' &&
                        Object.values(levelNotes.main || {}).some(n => n);

                      if (!hasNotes) return null;

                      return (
                        <div key={level.id} className="p-4 bg-charcoal-50 rounded-xl">
                          <p className="text-sm font-medium text-charcoal-700 mb-2 flex items-center gap-2">
                            <span>{level.icon}</span> {level.shortLabel}
                          </p>
                          <div className="space-y-2 text-sm text-charcoal-600">
                            {Object.entries(levelNotes.main || {}).map(([id, note]) =>
                              note ? <p key={id}>"{note}"</p> : null
                            )}
                          </div>
                        </div>
                      );
                    })}

                    {/* No notes message */}
                    {!selectedRating.overallNotes && !EVALUATION_LEVELS.some(level => {
                      const levelNotes = selectedRating.notes?.[level.id];
                      return levelNotes && typeof levelNotes === 'object' &&
                        Object.values(levelNotes.main || {}).some(n => n);
                    }) && (
                      <p className="text-sm text-charcoal-400 text-center py-4">
                        Keine Kommentare vorhanden
                      </p>
                    )}
                  </div>
                )}
              </div>
            </Modal>
          );
        })()}

        {/* Y-Achsen-Labels (Sterne: 5 oben = beste, 0 unten = schlechteste) */}
        <div
          className="absolute top-0 left-0 h-full flex flex-col justify-between text-xs text-charcoal-400 py-1 pointer-events-none"
          aria-hidden="true"
        >
          <span>5★</span>
          <span>2.5★</span>
          <span>0</span>
        </div>
      </div>

      {/* Sub-Graphen: je einer pro aktiver Kategorie (Agentur / Team / Chemie) */}
      {OVERLAY_LEVELS.map((level) => {
        const levelActive = enabledLevels.has(level);
        const levelCrits = LEVEL_CRITERIA[level];
        const activeCritIds = levelCrits
          .map((c) => c.id)
          .filter((id) => enabledCriteria.has(`${level}:${id}`));
        if (!levelActive && activeCritIds.length === 0) return null;

        const style = LEVEL_STYLES[level];
        const subHeight = 90;
        const subPadding = 14;
        const getSubY = (score: number): number => {
          const normalized = (MAX_STARS - score) / STAR_RANGE;
          return subPadding + normalized * (subHeight - 2 * subPadding);
        };
        const levelPoints = sortedRatings.map((r, idx) => {
          const v = getOverlayLevelScore(r.scores, level);
          if (v === null || Number.isNaN(v)) return null;
          return { x: getX(idx), y: getSubY(v), value: v };
        });
        const critLines = activeCritIds.map((critId) => {
          const points = sortedRatings.map((r, idx) => {
            const v = getCriterionScore(r.scores, level, critId);
            if (v === null || Number.isNaN(v)) return null;
            return { x: getX(idx), y: getSubY(v), value: v };
          });
          const critIndex = levelCrits.findIndex((c) => c.id === critId);
          const dashPatterns = ['4 2', '6 3', '2 3', '8 3 2 3', '10 4', '3 2 1 2', '5 5', '12 3 2 3', '7 2 2 2'];
          return {
            critId,
            label: levelCrits[critIndex]?.label ?? critId,
            dash: dashPatterns[critIndex % dashPatterns.length],
            path: buildPath(points),
          };
        });
        const levelPath = buildPath(levelPoints);

        return (
          <div key={`subgraph-${level}`} className="pt-2">
            <div className="flex items-center justify-between mb-1 px-1">
              <div className={`inline-flex items-center gap-1.5 text-xs font-medium ${style.textClass}`}>
                <span aria-hidden="true">{style.icon}</span>
                <span>{style.label}</span>
              </div>
              {critLines.length > 0 && (
                <div className="flex flex-wrap items-center gap-x-2 gap-y-0.5 text-xs text-charcoal-500">
                  {critLines.map((c) => (
                    <span key={c.critId} className="inline-flex items-center gap-1">
                      <span
                        className="inline-block"
                        style={{
                          width: 14,
                          height: 0,
                          borderTop: `1.5px dashed ${style.color}`,
                        }}
                        aria-hidden="true"
                      />
                      {c.label}
                    </span>
                  ))}
                </div>
              )}
            </div>
            <div className="relative" style={{ height: subHeight }}>
              <svg
                viewBox={`0 0 100 ${subHeight}`}
                preserveAspectRatio="none"
                className="w-full h-full"
              >
                {/* Hilfslinien */}
                {[subPadding, subHeight / 2, subHeight - subPadding].map((y) => (
                  <line key={y} x1="0" y1={y} x2="100" y2={y} stroke="#e5e7eb" strokeWidth="0.5" vectorEffect="non-scaling-stroke" />
                ))}

                {/* Kriterien-Linien (hinter der Level-Linie) */}
                {critLines.map((c) =>
                  c.path ? (
                    <path
                      key={`subcrit-${level}-${c.critId}`}
                      d={c.path}
                      fill="none"
                      stroke={style.color}
                      strokeOpacity="0.7"
                      strokeWidth="1.5"
                      strokeDasharray={c.dash}
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      vectorEffect="non-scaling-stroke"
                    />
                  ) : null,
                )}

                {/* Level-Linie */}
                {levelActive && levelPath && (
                  <path
                    d={levelPath}
                    fill="none"
                    stroke={style.color}
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    vectorEffect="non-scaling-stroke"
                  />
                )}

                {/* Level-Datenpunkte */}
                {levelActive && levelPoints.map((p, i) =>
                  p ? (
                    <circle
                      key={`sub-ldot-${level}-${i}`}
                      cx={p.x}
                      cy={p.y}
                      r="1.5"
                      fill={style.color}
                      vectorEffect="non-scaling-stroke"
                    />
                  ) : null,
                )}
              </svg>

              <div
                className="absolute top-0 left-0 h-full flex flex-col justify-between text-xs text-charcoal-400 py-1 pointer-events-none"
                aria-hidden="true"
              >
                <span>5★</span>
                <span>0</span>
              </div>
            </div>
          </div>
        );
      })}

      {/* Zeitstrahl mit Daten */}
      <div className="relative">
        {/* Verbindungslinie */}
        <div className="absolute top-3 left-0 right-0 h-0.5 bg-charcoal-200" />

        {/* Datenpunkte auf dem Zeitstrahl */}
        <div className="relative flex justify-between">
          {sortedRatings.map((rating, index) => {
            const score = calculateCherryScore(rating.scores);
            const prevRating = sortedRatings[index - 1];
            const prevScore = prevRating
              ? calculateCherryScore(prevRating.scores)
              : null;
            const diff = prevScore !== null ? score - prevScore : null;

            return (
              <button
                type="button"
                key={rating.id}
                className="flex flex-col items-center cursor-pointer hover:bg-charcoal-50 rounded-lg p-1 -m-1 transition-colors focus:outline-none focus:ring-2 focus:ring-cherry-500 focus:ring-offset-2"
                style={{
                  width: sortedRatings.length === 1 ? '100%' : undefined,
                }}
                onClick={() => handleRatingClick(rating, index)}
                title={`Bewertung von ${rating.ratedBy?.displayName || 'Unbekannt'} anzeigen`}
                aria-label={`Bewertung vom ${formatDate(rating.createdAt)}, Score ${score.toFixed(1)} Sterne${rating.ratedBy ? `, von ${rating.ratedBy.displayName}` : ''}`}
              >
                {/* Avatar / Punkt */}
                {rating.ratedBy ? (
                  <div className="border-2 border-white shadow-md rounded-full z-10">
                    <UserAvatar
                      avatar={rating.ratedBy.avatar}
                      displayName={rating.ratedBy.displayName}
                      size="sm"
                    />
                  </div>
                ) : (
                  <div className="w-6 h-6 rounded-full bg-cherry-600 border-4 border-white shadow-md flex items-center justify-center z-10">
                    <div className="w-2 h-2 rounded-full bg-white" />
                  </div>
                )}

                {/* Score als Sterne */}
                <div className="mt-2 text-center">
                  <p className={`text-lg font-bold ${getScoreColorClass(score)}`}>
                    {score.toFixed(1)} ★
                    <span className="sr-only">
                      {' '}({getScoreLevel(score) === 'good' ? 'sehr gut' : getScoreLevel(score) === 'medium' ? 'mittel' : 'schwach'})
                    </span>
                  </p>
                  {diff !== null && diff !== 0 && (
                    // Sterne-Logik: positive diff = besser (grün), negative diff = schlechter (rot)
                    <p
                      className={`text-xs font-medium ${
                        diff > 0 ? 'text-green-600' : 'text-red-600'
                      }`}
                    >
                      {diff > 0 ? '+' : ''}{diff.toFixed(1)}
                    </p>
                  )}
                </div>

                {/* Bewertender */}
                {rating.ratedBy && (
                  <p className="text-xs text-charcoal-600 mt-1 whitespace-nowrap font-medium">
                    {rating.ratedBy.displayName.split(' ')[0]}
                  </p>
                )}

                {/* Datum */}
                <p className="text-xs text-charcoal-400 whitespace-nowrap">
                  {formatDate(rating.createdAt)}
                </p>
              </button>
            );
          })}
        </div>
      </div>
    </div>
  );
});

export default RatingTimeline;
